<template>
  <div class="form-page-container">
    <PremiumPage
      title="Create New Bill"
      subtitle="Generate recurring or one-time invoices for members"
      showBack
      show-footer-buttons
      submit-label="Submit"
      :loading="loading"
      @cancel="$router.go(-1)"
      @submit="saveBill"
    >
      <div class="pa-4">
        <v-form>
          <div class="half-width-wrap">
            <!-- ── Bill Type & Generate For ── -->
            <v-row>
              <v-col cols="12">
                <div class="premium-field-label mb-3">Target Audience</div>
                <div class="radio-row mb-3">
                  <!-- Mixed (Both) -->
                  <template v-if="societyUnitType == 3">
                    <label
                      class="radio-option"
                      :class="{ active: sendto === 'all' }"
                    >
                      <input type="radio" value="all" v-model="sendto" hidden />
                      <v-icon small class="mr-1"
                        >mdi-account-group-outline</v-icon
                      >
                      All Units
                    </label>
                    <label
                      class="radio-option"
                      :class="{ active: sendto === 'houses' }"
                    >
                      <input
                        type="radio"
                        value="houses"
                        v-model="sendto"
                        hidden
                      />
                      <v-icon small class="mr-1">mdi-home-outline</v-icon>
                      All Houses
                    </label>
                    <label
                      class="radio-option"
                      :class="{ active: sendto === 'shops' }"
                    >
                      <input
                        type="radio"
                        value="shops"
                        v-model="sendto"
                        hidden
                      />
                      <v-icon small class="mr-1">mdi-store-outline</v-icon>
                      All Shops
                    </label>
                    <label
                      class="radio-option"
                      :class="{ active: sendto === 'selected' }"
                    >
                      <input
                        type="radio"
                        value="selected"
                        v-model="sendto"
                        hidden
                      />
                      <v-icon small class="mr-1"
                        >mdi-cursor-default-click-outline</v-icon
                      >
                      Specific Units
                    </label>
                  </template>
                  <!-- Residential or Commercial -->
                  <template v-else>
                    <label
                      class="radio-option"
                      :class="{ active: sendto === 'all' }"
                    >
                      <input type="radio" value="all" v-model="sendto" hidden />
                      <v-icon small class="mr-1"
                        >mdi-account-group-outline</v-icon
                      >
                      All Units
                    </label>
                    <label
                      class="radio-option"
                      :class="{ active: sendto === 'selected' }"
                    >
                      <input
                        type="radio"
                        value="selected"
                        v-model="sendto"
                        hidden
                      />
                      <v-icon small class="mr-1"
                        >mdi-cursor-default-click-outline</v-icon
                      >
                      Selected Units
                    </label>
                  </template>
                </div>
              </v-col>
            </v-row>

            <!-- ── Selective Pickers (Only when Selected) ── -->
            <v-expand-transition>
              <div
                v-if="sendto === 'selected'"
                class="selective-selectors mb-6"
              >
                <!-- Mixed -->
                <template v-if="societyUnitType == 3">
                  <v-row dense>
                    <v-col cols="12" v-if="useBlocks">
                      <div class="premium-field-label mb-2">
                        Filter Houses by Block
                      </div>
                      <v-select
                        v-model="houseBlock"
                        :items="flats"
                        item-text="name"
                        item-value="id"
                        placeholder="Select Block"
                        outlined
                        dense
                        hide-details="auto"
                        class="premium-select"
                        clearable
                      />
                    </v-col>
                  </v-row>
                  <v-row dense class="mt-2">
                    <v-col cols="12">
                      <div class="premium-field-label mb-2">Select Houses</div>
                      <v-select
                        v-model="selectedHouses"
                        :items="houseList"
                        item-text="display_number"
                        item-value="id"
                        multiple
                        chips
                        dense
                        deletable-chips
                        outlined
                        hide-details="auto"
                        class="premium-select"
                        placeholder="Choose Houses"
                      >
                        <template v-slot:prepend-item>
                          <v-list-item ripple @click="toggleAllHouses">
                            <v-list-item-action>
                              <v-icon
                                :color="
                                  selectedHouses.length > 0 ? 'primary' : ''
                                "
                                >{{ iconHousesSel }}</v-icon
                              >
                            </v-list-item-action>
                            <v-list-item-title>Select All</v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>
                    <v-col cols="12">
                      <div class="premium-field-label mb-2">Select Shops</div>
                      <v-select
                        v-model="selectedShops"
                        :items="shopList"
                        item-text="display_number"
                        item-value="id"
                        multiple
                        chips
                        dense
                        deletable-chips
                        outlined
                        hide-details="auto"
                        class="premium-select"
                        placeholder="Choose Shops"
                      >
                        <template v-slot:prepend-item>
                          <v-list-item ripple @click="toggleAllShops">
                            <v-list-item-action>
                              <v-icon
                                :color="
                                  selectedShops.length > 0 ? 'primary' : ''
                                "
                                >{{ iconShopsSel }}</v-icon
                              >
                            </v-list-item-action>
                            <v-list-item-title>Select All</v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </template>
                <!-- Residential/Commercial -->
                <template v-else>
                  <v-row dense>
                    <v-col cols="12" v-if="useBlocks">
                      <div class="premium-field-label mb-2">
                        Filter Units by Block
                      </div>
                      <v-select
                        v-model="bill.flat_id"
                        :items="flats"
                        item-text="name"
                        item-value="id"
                        outlined
                        dense
                        hide-details="auto"
                        class="premium-select"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12">
                      <div class="premium-field-label mb-2">Select Units</div>
                      <v-select
                        v-model="bill.members"
                        :items="houses"
                        item-text="display_number"
                        item-value="id"
                        multiple
                        chips
                        dense
                        deletable-chips
                        outlined
                        hide-details="auto"
                        class="premium-select"
                      >
                        <template v-slot:prepend-item>
                          <v-list-item ripple @click="toggleHouses">
                            <v-list-item-action>
                              <v-icon
                                :color="
                                  bill.members.length > 0 ? 'primary' : ''
                                "
                                >{{ iconHouses }}</v-icon
                              >
                            </v-list-item-action>
                            <v-list-item-title>Select All</v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </template>
              </div>
            </v-expand-transition>

            <!-- ── Bill Details ── -->
            <v-divider class="my-8" />

            <v-row>
              <v-col cols="12">
                <PremiumInput
                  v-model="bill.title"
                  label="Bill Title"
                  placeholder="e.g. Monthly Maintenance Jan 2024"
                  icon="mdi-format-title"
                  v-validate="'required'"
                  name="title"
                  :error="errors.first('title') || serverSideErrors.title"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <div class="premium-field-label mb-2">Description</div>
                <v-textarea
                  v-model="bill.description"
                  name="description"
                  placeholder="Brief summary of charges..."
                  v-validate="'required'"
                  rows="3"
                  outlined
                  hide-details="auto"
                  class="premium-textarea"
                  :error="
                    errors.first('description') != null ||
                    serverSideErrors.description != null
                  "
                  :error-messages="
                    errors.first('description') || serverSideErrors.description
                  "
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <PremiumInput
                  v-model="bill.amount"
                  label="Amount for Owners (₹)"
                  placeholder="0.00"
                  icon="mdi-currency-inr"
                  @keyup="setRentalVal()"
                  v-validate="'required|numeric|max_value:9999999|min_value:1'"
                  name="amount"
                  type="number"
                  :error="errors.first('amount') || serverSideErrors.amount"
                />
              </v-col>
              <v-col cols="12">
                <PremiumInput
                  v-model="bill.amount_for_rental"
                  label="Amount for Tenants (₹)"
                  placeholder="0.00"
                  icon="mdi-currency-inr"
                  v-validate="'required|numeric|max_value:9999999|min_value:1'"
                  name="amount for rental"
                  type="number"
                  :error="
                    errors.first('amount for rental') ||
                    serverSideErrors.amount_for_rental
                  "
                />
              </v-col>
              <v-col cols="12">
                <div class="premium-field-label mb-3">Bill Schedule</div>
                <div class="radio-row ">
                  <label
                    v-for="t in types"
                    :key="t.value"
                    class="radio-option"
                    :class="{ active: bill.type === t.value }"
                  >
                    <input
                      type="radio"
                      :value="t.value"
                      v-model="bill.type"
                      hidden
                    />
                    <v-icon small class="mr-2">{{ t.icon }}</v-icon>
                    {{ t.text }}
                  </label>
                </div>
              </v-col>
            </v-row>

            <!-- ── Scheduling Fields ── -->
            <v-row class="mt-4">
              <!-- One-time Date -->
              <v-col cols="12" v-if="bill.type == 1">
                <PremiumDatePicker
                  v-model="bill.one_time_date"
                  label="Billing Date"
                  placeholder="Select Date"
                />
              </v-col>

              <!-- Recurring Fields -->
              <v-col cols="12" v-if="bill.type == 2 || bill.type == 3">
                <div class="premium-field-label mb-2">Day of Month</div>
                <v-select
                  v-model="bill.day_of_month"
                  :items="dates"
                  placeholder="Select Day"
                  outlined
                  dense
                  hide-details="auto"
                  class="premium-select"
                />
              </v-col>
              <v-col cols="12" v-if="bill.type == 3">
                <div class="premium-field-label mb-2">Billing Month</div>
                <v-select
                  v-model="bill.month"
                  :items="months"
                  item-text="name"
                  item-value="id"
                  placeholder="Select Month"
                  outlined
                  dense
                  hide-details="auto"
                  class="premium-select"
                />
              </v-col>

              <v-col cols="12">
                <PremiumInput
                  v-model="bill.due_days"
                  label="Due Days"
                  placeholder="e.g. 15"
                  icon="mdi-clock-alert-outline"
                  v-validate="'required|numeric|max_value:364'"
                  name="due days"
                  type="number"
                  :error="errors.first('due days') || serverSideErrors.due_days"
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <div class="premium-field-label mb-3">
                  Attachments / Sample Bill
                </div>
                <input
                  type="file"
                  ref="attachInput"
                  multiple
                  accept="image/*,.pdf"
                  hidden
                  @change="onAttachChange"
                />
                <!-- Drop Zone -->
                <div
                  class="attach-dropzone"
                  :class="{ 'attach-dropzone-active': dragging }"
                  @click="$refs.attachInput.click()"
                  @dragover.prevent="dragging = true"
                  @dragleave="dragging = false"
                  @drop.prevent="onDrop"
                >
                  <v-icon size="28" color="var(--tx3)">mdi-paperclip</v-icon>
                  <div class="dropzone-label">Click or drag files here</div>
                  <div class="dropzone-hint">JPG, PNG, PDF allowed</div>
                </div>

                <!-- Preview Grid -->
                <div
                  v-if="attachPreviews.length"
                  class="attach-preview-grid mt-3"
                >
                  <div
                    v-for="(p, i) in attachPreviews"
                    :key="i"
                    class="attach-preview-item"
                  >
                    <img
                      v-if="p.isImage"
                      :src="p.url"
                      class="attach-preview-img"
                    />
                    <div v-else class="attach-preview-pdf">
                      <v-icon size="32" color="#EF4444"
                        >mdi-file-pdf-box</v-icon
                      >
                      <span class="attach-preview-name">{{ p.name }}</span>
                    </div>
                    <button
                      type="button"
                      class="attach-remove-btn"
                      @click.stop="removeAttach(i)"
                    >
                      <v-icon size="14" color="white">mdi-close</v-icon>
                    </button>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-form>
      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";

export default {
  name: "AddBill",
  components: { PremiumPage, PremiumInput, PremiumDatePicker },
  data() {
    return {
      moment: moment,
      user: {},
      loading: false,
      serverSideErrors: {},
      bill: {
        type: 1,
        flat_id: null,
        title: "",
        description: "",
        documents: [],
        members: [],
        amount: "",
        amount_for_rental: "",
        one_time_date: new Date().toISOString().slice(0, 10),
        day_of_month: null,
        month: null,
        due_days: "",
      },
      sendto: "all",
      houseBlock: null,
      selectedHouses: [],
      selectedShops: [],
      flats: [],
      houses: [],
      houseList: [],
      shopList: [],
      menu: false,
      attachPreviews: [],
      dragging: false,
      dates: Array.from({ length: 28 }, (_, i) => i + 1),
      types: [
        { text: "One Time", value: 1, icon: "mdi-calendar-check-outline" },
        { text: "Monthly", value: 2, icon: "mdi-calendar-month-outline" },
        { text: "Yearly", value: 3, icon: "mdi-calendar-sync-outline" },
      ],
      months: [
        { id: 1, name: "January" },
        { id: 2, name: "February" },
        { id: 3, name: "March" },
        { id: 4, name: "April" },
        { id: 5, name: "May" },
        { id: 6, name: "June" },
        { id: 7, name: "July" },
        { id: 8, name: "August" },
        { id: 9, name: "September" },
        { id: 10, name: "October" },
        { id: 11, name: "November" },
        { id: 12, name: "December" },
      ],
    };
  },
  computed: {
    ...mapGetters("society", ["useBlocks", "unitLabel", "unitType"]),
    societyUnitType() {
      return this.unitType;
    },
    allHousesSelected() {
      return (
        this.bill.members.length > 0 &&
        this.bill.members.length === this.houses.length
      );
    },
    iconHouses() {
      return this.allHousesSelected
        ? "mdi-close-box"
        : this.bill.members.length > 0
        ? "mdi-minus-box"
        : "mdi-checkbox-blank-outline";
    },
    allHousesSelMixed() {
      return (
        this.houseList.length > 0 &&
        this.selectedHouses.length === this.houseList.length
      );
    },
    iconHousesSel() {
      return this.allHousesSelMixed
        ? "mdi-close-box"
        : this.selectedHouses.length > 0
        ? "mdi-minus-box"
        : "mdi-checkbox-blank-outline";
    },
    allShopsSel() {
      return (
        this.shopList.length > 0 &&
        this.selectedShops.length === this.shopList.length
      );
    },
    iconShopsSel() {
      return this.allShopsSel
        ? "mdi-close-box"
        : this.selectedShops.length > 0
        ? "mdi-minus-box"
        : "mdi-checkbox-blank-outline";
    },
  },
  watch: {
    sendto() {
      this.bill.members = [];
      this.selectedHouses = [];
      this.selectedShops = [];
      if (this.sendto === "selected") {
        if (this.societyUnitType == 3) this.loadMixedUnits();
        else if (!this.useBlocks) this.getHouses();
      }
    },
    "bill.flat_id"() {
      if (this.sendto === "selected" && this.societyUnitType != 3)
        this.getHouses();
    },
    houseBlock() {
      if (this.houseBlock) this.loadHouseList();
      else this.loadMixedUnits();
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    setRentalVal() {
      this.bill.amount_for_rental = this.bill.amount;
    },
    toggleHouses() {
      const currentIds = this.houses.map((h) => h.id);
      if (currentIds.every((id) => this.bill.members.includes(id)))
        this.bill.members = this.bill.members.filter(
          (id) => !currentIds.includes(id),
        );
      else
        this.bill.members = [...new Set([...this.bill.members, ...currentIds])];
    },
    toggleAllHouses() {
      const currentIds = this.houseList.map((h) => h.id);
      if (currentIds.every((id) => this.selectedHouses.includes(id)))
        this.selectedHouses = this.selectedHouses.filter(
          (id) => !currentIds.includes(id),
        );
      else
        this.selectedHouses = [
          ...new Set([...this.selectedHouses, ...currentIds]),
        ];
    },
    toggleAllShops() {
      const currentIds = this.shopList.map((h) => h.id);
      if (currentIds.every((id) => this.selectedShops.includes(id)))
        this.selectedShops = this.selectedShops.filter(
          (id) => !currentIds.includes(id),
        );
      else
        this.selectedShops = [
          ...new Set([...this.selectedShops, ...currentIds]),
        ];
    },
    getFlats() {
      this.$axios.get(`/societyadmin/flats`).then((r) => {
        if (r.data.status) this.flats = r.data.flats;
      });
    },
    getHouses() {
      this.$axios
        .get(`/societyadmin/houses`, { params: { flat_id: this.bill.flat_id } })
        .then((r) => {
          if (r.data.status)
            this.houses = r.data.houses.map((h) => this.formatUnit(h));
        });
    },
    loadMixedUnits() {
      this.$axios.get(`/societyadmin/houses`).then((r) => {
        if (r.data.status) {
          const all = r.data.houses.map((h) => this.formatUnit(h));
          this.houseList = all.filter((h) => h.unit_type != 2);
          this.shopList = all.filter((h) => h.unit_type == 2);
        }
      });
    },
    loadHouseList() {
      this.$axios
        .get(`/societyadmin/houses`, { params: { flat_id: this.houseBlock } })
        .then((r) => {
          if (r.data.status)
            this.houseList = r.data.houses
              .map((h) => this.formatUnit(h))
              .filter((h) => h.unit_type != 2);
        });
    },
    formatUnit(h) {
      let disp = "";
      if (h.flat) disp += h.flat.name + " - ";
      if (this.societyUnitType == 3)
        disp += h.unit_type == 2 ? "Shop " : "House ";
      disp += h.house_number;
      return { ...h, display_number: disp };
    },
    buildSendToValue() {
      if (this.sendto === "houses") return "4";
      if (this.sendto === "shops") return "5";
      if (this.sendto === "selected") return "3";
      return "1";
    },
    onAttachChange(e) {
      this.addFiles(Array.from(e.target.files));
      e.target.value = "";
    },
    onDrop(e) {
      this.dragging = false;
      this.addFiles(Array.from(e.dataTransfer.files));
    },
    addFiles(files) {
      files.forEach((file) => {
        this.bill.documents.push(file);
        const isImage = file.type.startsWith("image/");
        if (isImage) {
          const url = URL.createObjectURL(file);
          this.attachPreviews.push({ url, name: file.name, isImage: true });
        } else {
          this.attachPreviews.push({
            url: null,
            name: file.name,
            isImage: false,
          });
        }
      });
    },
    removeAttach(i) {
      this.bill.documents.splice(i, 1);
      this.attachPreviews.splice(i, 1);
    },
    saveBill() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.loading = true;
        const formData = new FormData();
        formData.append("type", this.bill.type);
        formData.append("title", this.bill.title);
        formData.append("description", this.bill.description);
        formData.append("send_to", this.buildSendToValue());
        formData.append("amount", this.bill.amount);
        formData.append("amount_for_rental", this.bill.amount_for_rental);
        formData.append(
          "one_time_date",
          this.bill.type == 1
             ? moment(this.bill.one_time_date).utc().valueOf()
            : "",
        );
        formData.append(
          "day_of_month",
          this.bill.type != 1 ? this.bill.day_of_month || "" : "",
        );
        formData.append(
          "month",
          this.bill.type == 3 ? this.bill.month || "" : "",
        );
        formData.append("due_days", this.bill.due_days);

        let members =
          this.sendto === "selected"
            ? this.societyUnitType == 3
              ? [...this.selectedHouses, ...this.selectedShops]
              : this.bill.members
            : [];
        members.forEach((m) => formData.append("members[]", m));
        if (this.bill.documents)
          this.bill.documents.forEach((d) => formData.append("documents[]", d));

        this.$axios
          .post("/societyadmin/bills/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.loading = false;
            if (response.data.status) {
              this.setAlert({
                showAlert: true,
                message: response.data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.$router.push({ path: "/societyadmin/bills" });
            } else {
              this.serverSideErrors = {};
              if (response.data.errors)
                response.data.errors.forEach((e) => {
                  this.serverSideErrors[e.field] = e.message;
                });
              else this.showErr(response.data.message);
            }
          })
          .catch(() => {
            this.loading = false;
          });
      });
    },
    showErr(msg) {
      this.setAlert({
        showAlert: true,
        message: msg,
        variant: "red accent-3",
        timeout: 2000,
      });
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.getFlats();
  },
};
</script>

<style scoped>
.form-page-container {
  padding: 24px;
}

/* ── 50% width wrapper ── */
.half-width-wrap {
  max-width: 50%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.premium-field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Radio Controls ── */
.radio-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface2);
  font-size: 13.5px;
  font-weight: 700;
  color: var(--tx2);
  cursor: pointer;
  transition: all 0.2s;
}
.radio-option:hover {
  border-color: var(--brand);
  transform: translateY(-1px);
}
.radio-option.active {
  border-color: var(--brand);
  background: var(--brand-light, rgba(16, 185, 129, 0.08));
  color: var(--brand);
}

/* ── Custom Textarea ── */
::v-deep .premium-textarea .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2) !important;
  border: 1px solid var(--border) !important;
}
::v-deep .premium-textarea fieldset {
  border: none;
}

/* ── Select ── */
::v-deep .premium-select .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2) !important;
  border: 1px solid var(--border) !important;
}
::v-deep .premium-select fieldset {
  border: none;
}

/* ── Attachment Uploader ── */
.attach-dropzone {
  border: 2px dashed var(--border, #e2e8f0);
  border-radius: 14px;
  background: var(--surface2, #f8fafc);
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.attach-dropzone:hover {
  border-color: var(--brand);
  background: rgba(13, 153, 114, 0.04);
}
.attach-dropzone-active {
  border-color: var(--brand) !important;
  background: rgba(13, 153, 114, 0.06) !important;
}
.dropzone-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2);
  margin-top: 4px;
}
.dropzone-hint {
  font-size: 11.5px;
  color: var(--tx3);
  font-weight: 500;
}

.attach-preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.attach-preview-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--surface2);
}
.attach-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.attach-preview-pdf {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px;
}
.attach-preview-name {
  font-size: 9px;
  color: var(--tx3);
  text-align: center;
  word-break: break-all;
  line-height: 1.2;
}
.attach-remove-btn {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}
.attach-remove-btn:hover {
  background: #ef4444;
}

.cancel-btn {
  color: var(--tx2);
  font-weight: 600;
  text-transform: none;
}
.save-btn {
  border-radius: 12px !important;
  font-weight: 800;
  height: 46px !important;
  text-transform: none !important;
}
</style>
